"use client";

import { ReactNode, useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

interface ApproachOnScrollProps {
  className?: string;
  children: ReactNode;
}

// While smaller than its box the content would show its side edges, so they fade out until the
// end, when --approach-edge is cleared and the mask stops applying.
const EDGE = "max(var(--approach-edge), var(--approach-rest-edge, 0px))";
const EDGE_MASK = `linear-gradient(to right, transparent, #000 ${EDGE}, #000 calc(100% - ${EDGE}), transparent)`;

/**
 * Scroll-linked "walking up to it" entrance: the content starts a little smaller and blurred and
 * reaches its real size as the page scrolls down. Progress only moves forward, so scrolling back
 * up never undoes it. It grows from its bottom edge, which stays in place.
 */
export default function ApproachOnScroll({ className, children }: ApproachOnScrollProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const tween = gsap.fromTo(
      el,
      { scale: 0.92, filter: "blur(8px)", "--approach-edge": "16%", transformOrigin: "50% 100%" },
      {
        scale: 1,
        filter: "blur(0px)",
        "--approach-edge": "0%",
        ease: "none",
        paused: true,
        clearProps: "transform,transformOrigin,filter,--approach-edge",
      },
    );
    let reached = 0;

    const trigger = ScrollTrigger.create({
      trigger: el,
      start: "top bottom",
      end: "top 55%",
      onUpdate: (self) => {
        if (self.progress <= reached) return;
        reached = self.progress;
        // A short catch-up keeps fast scrolls smooth, like the scrub used by TextScrollFadeIn.
        gsap.to(tween, { progress: reached, duration: 0.4, ease: "power1.out", overwrite: true });
        if (reached >= 1) trigger.kill();
      },
    });

    // Opened already past the section (reload, anchor link): show the final state.
    if (trigger.progress >= 1) {
      tween.progress(1);
      trigger.kill();
    }

    return () => {
      trigger.kill();
      gsap.killTweensOf(tween);
      tween.kill();
      gsap.set(el, { clearProps: "transform,transformOrigin,filter,--approach-edge" });
    };
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{ maskImage: EDGE_MASK, WebkitMaskImage: EDGE_MASK }}>
      {children}
    </div>
  );
}
